import React, { useCallback, useMemo } from 'react';

// Hooks
import { useData } from '../App/useData';

// Data
import { ApiEndpoints } from '../../data/ApiEndpoints';

// Constants
import { LocalStorageKeys } from '../../constants/Utils/LocalStorage';
import { getTableColumns } from '../../constants/Utils/UIFunctions';

// Components
import { Translated } from '../../components/UI/Core';
import { BasicIcon } from '../../components/UI/Icon/BasicIcon';
import { appBreadcrumbs } from '../../data/Breadcrumbs/Breadcrumbs';
import { parkingTransactionTableColumns, parkingUsageTableColumns } from '../../data/Table/ParkingUsageTableColumns';
import { useDownloadReportButton } from './useDownloadReportButton';
import { useParkingTableFilter } from './useParkingTableFilter';

// Hook
export const useParkingUsageList = <T extends object>() => {
  // Data
  const {
    data: parkingUsageData,
    pageCount: parkingUsagePageCount,
    totalCount: parkingUsageTotalCount,
    fetch: parkingUsageFetchPage,
    pending,
  } = useData(ApiEndpoints.parking.parkingUsage, null);

  const {
    data: parkingTransactionData,
    pageCount: parkingTransactionPageCount,
    totalCount: parkingTransactionTotalCount,
    fetch: parkingTransactionFetchPage,
  } = useData(ApiEndpoints.parking.parkingTransaction, null);

  // Table Filter
  const { FilterSelect, filterValues } = useParkingTableFilter();

  const { DownloadReportButton } = useDownloadReportButton();

  const fetchParkingUsage = useCallback(
    (args: any) => {
      return parkingUsageFetchPage({
        ...args,
      });
    },
    [parkingUsageFetchPage]
  );

  const fetchParkingTransaction = useCallback(
    (args: any) => {
      return parkingTransactionFetchPage({
        ...args,
      });
    },
    [parkingTransactionFetchPage]
  );

  // Fetch + Append Filter Data
  const fetchParkingTransactionFilters = useCallback(
    (args: any) => fetchParkingTransaction({ ...args, ...filterValues }),
    [fetchParkingTransaction, filterValues]
  );

  // Props
  const getBreadcrumbMenuProps = useCallback(() => ({ breadcrumbs: appBreadcrumbs.parkingUsage.list }), []);

  const getParkingUsageDataProps = useCallback(
    () => ({
      storageKey: LocalStorageKeys.ParkingUsage.table.hiddenColumns,
      title: (
        <>
          <BasicIcon className="icon icon-chart-pie" style={{ marginRight: 8 }} />
          <Translated id="appMenu.parkingUsage" />
        </>
      ),
      data: parkingUsageData ?? [],
      columns: getTableColumns(parkingUsageTableColumns(), LocalStorageKeys.ParkingUsage.table.hiddenColumns),
      fetchData: fetchParkingUsage,
    }),
    [fetchParkingUsage, parkingUsageData]
  );

  const getParkingTransactionDataProps = useCallback(
    () => ({
      storageKey: LocalStorageKeys.ParkingTransaction.table.hiddenColumns,
      title: (
        <>
          <BasicIcon className="icon icon-transfer" style={{ marginRight: 8 }} />
          <Translated id="appMenu.transaction" />
        </>
      ),
      data: parkingTransactionData ?? [],
      columns: getTableColumns(
        parkingTransactionTableColumns(),
        LocalStorageKeys.ParkingTransaction.table.hiddenColumns
      ),
      // fetchData: fetchParkingTransaction,
      fetchData: fetchParkingTransactionFilters,
    }),
    [fetchParkingTransactionFilters, parkingTransactionData]
  );

  const getParkingUsageTableProps = useCallback(
    () => ({
      pending,
      pageCount: parkingUsagePageCount,
      totalCount: parkingUsageTotalCount,
    }),
    [pending, parkingUsagePageCount, parkingUsageTotalCount]
  );

  const getParkingTransactionTableProps = useCallback(
    () => ({
      pending,
      pageCount: parkingTransactionPageCount,
      totalCount: parkingTransactionTotalCount,
    }),
    [pending, parkingTransactionPageCount, parkingTransactionTotalCount]
  );

  const getParkingUsageCrudProps = useCallback(
    () => ({
      onRowClick: () => null,
    }),
    []
  );

  const getParkingTransactionCrudProps = useCallback(
    () => ({
      onRowClick: () => null,
      FilterSelect,
      DownloadReportButton,
    }),
    [FilterSelect, DownloadReportButton]
  );

  return useMemo(
    () => ({
      pending,
      getBreadcrumbMenuProps,
      getParkingUsageDataProps,
      getParkingTransactionDataProps,
      getParkingUsageTableProps,
      getParkingTransactionTableProps,
      getParkingUsageCrudProps,
      getParkingTransactionCrudProps,
    }),
    [
      pending,
      getBreadcrumbMenuProps,
      getParkingUsageDataProps,
      getParkingTransactionDataProps,
      getParkingUsageTableProps,
      getParkingTransactionTableProps,
      getParkingUsageCrudProps,
      getParkingTransactionCrudProps,
    ]
  );
};
